import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Lock, Send } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../context/AuthContext";
import { ShippingSettingsCard } from "./ShippingSettingsCard";

export const AdminSettings = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({ etransfer_email: "", etransfer_name: "", instructions: "", volume_discounts: [], free_shipping_threshold: 300, shipping_fee: 20 });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTest, setEmailTest] = useState(null);

  useEffect(() => {
    api.get("/admin/settings")
      .then(({ data }) => setForm({
        ...data,
        etransfer_steps: (data.etransfer_steps || []).map((s) => ({ text: s, _key: crypto.randomUUID() })),
        volume_discounts: (data.volume_discounts || []).map((t) => ({ ...t, _key: crypto.randomUUID() })),
        shipping_rate_table: (data.shipping_rate_table || []).map((t) => ({ ...t, _key: crypto.randomUUID() })),
        notification_emails: (data.notification_emails || []).map((e) => ({ email: e, _key: crypto.randomUUID() })),
      }))
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!form.etransfer_email) {
      toast.error("E-Transfer email is required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      free_shipping_threshold: parseFloat(form.free_shipping_threshold) || 0,
      shipping_fee: parseFloat(form.shipping_fee) || 0,
      express_shipping_fee: parseFloat(form.express_shipping_fee) || 0,
      express_shipping_enabled: form.express_shipping_enabled ?? true,
      standard_shipping_eta: (form.standard_shipping_eta || "").trim() || "2–8 business days",
      express_shipping_eta: (form.express_shipping_eta || "").trim() || "1–2 business days",
      shipping_mode: form.shipping_mode || "automatic",
      shipping_rate_table: (form.shipping_rate_table || [])
        .map((t) => ({ min_qty: parseInt(t.min_qty, 10) || 0, standard: parseFloat(t.standard) || 0, express: parseFloat(t.express) || 0 }))
        .filter((t) => t.min_qty > 0)
        .sort((a, b) => a.min_qty - b.min_qty),
      rewards_enabled: !!form.rewards_enabled,
      rewards_earn_rate: parseFloat(form.rewards_earn_rate) || 0,
      rewards_redeem_rate: parseFloat(form.rewards_redeem_rate) || 0,
      card_payments_enabled: !!form.card_payments_enabled,
      card_fee_percent: parseFloat(form.card_fee_percent) || 0,
      card_fee_fixed: parseFloat(form.card_fee_fixed) || 0,
      low_stock_threshold: parseInt(form.low_stock_threshold, 10) || 0,
      bundles_visible: !!form.bundles_visible,
      etransfer_steps: form.etransfer_steps.map((s) => s.text.trim()).filter(Boolean),
      notification_emails: (form.notification_emails || []).map((e) => e.email.trim().toLowerCase()).filter(Boolean),
      volume_discounts: form.volume_discounts
        .map((t) => ({ min_qty: parseInt(t.min_qty, 10) || 0, percent: parseFloat(t.percent) || 0 }))
        .filter((t) => t.min_qty > 0 && t.percent > 0)
        .sort((a, b) => a.min_qty - b.min_qty),
    };
    try {
      await api.put("/admin/settings", payload);
      setForm((f) => ({
        ...f,
        etransfer_steps: payload.etransfer_steps.map((s) => ({ text: s, _key: crypto.randomUUID() })),
        volume_discounts: payload.volume_discounts.map((t) => ({ ...t, _key: crypto.randomUUID() })),
        shipping_rate_table: payload.shipping_rate_table.map((t) => ({ ...t, _key: crypto.randomUUID() })),
        notification_emails: payload.notification_emails.map((e) => ({ email: e, _key: crypto.randomUUID() })),
      }));
      toast.success("Settings saved — checkout page updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const setTier = (idx, key, val) => setForm((f) => ({
    ...f, volume_discounts: f.volume_discounts.map((t, i) => (i === idx ? { ...t, [key]: val } : t)),
  }));
  const addTier = () => setForm((f) => ({ ...f, volume_discounts: [...f.volume_discounts, { min_qty: "", percent: "", _key: crypto.randomUUID() }] }));
  const removeTier = (idx) => setForm((f) => ({ ...f, volume_discounts: f.volume_discounts.filter((_, i) => i !== idx) }));

  const setStep = (idx, text) => setForm((f) => ({
    ...f, etransfer_steps: f.etransfer_steps.map((s, i) => (i === idx ? { ...s, text } : s)),
  }));
  const addStep = () => setForm((f) => ({ ...f, etransfer_steps: [...f.etransfer_steps, { text: "", _key: crypto.randomUUID() }] }));
  const removeStep = (idx) => setForm((f) => ({ ...f, etransfer_steps: f.etransfer_steps.filter((_, i) => i !== idx) }));

  const setNotifyEmail = (idx, email) => setForm((f) => ({
    ...f, notification_emails: f.notification_emails.map((e, i) => (i === idx ? { ...e, email } : e)),
  }));
  const addNotifyEmail = () => setForm((f) => ({ ...f, notification_emails: [...(f.notification_emails || []), { email: "", _key: crypto.randomUUID() }] }));
  const removeNotifyEmail = (idx) => setForm((f) => ({ ...f, notification_emails: f.notification_emails.filter((_, i) => i !== idx) }));

  const sendTestEmail = async () => {
    setTestingEmail(true);
    setEmailTest(null);
    try {
      const { data } = await api.post("/admin/email/test");
      setEmailTest({ ok: true, to: data.to, sender: data.sender });
      toast.success(`Test email sent to ${data.to}`);
    } catch (err) {
      const error = err.response?.data?.detail || "Email could not be sent.";
      setEmailTest({ ok: false, error });
      toast.error("Test email failed");
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-8">
      {user?.is_super_admin ? (
        <div className="border border-[#DDD7CF] rounded-lg bg-white p-8" data-testid="admin-settings-form">
          <h2 className="font-display text-lg uppercase tracking-wide text-[#1A1A1A] mb-2">E-Transfer Payment Settings</h2>
          <p className="text-sm text-[#6E675F] mb-8">These details are shown to customers on the checkout and order confirmation pages.</p>

          <div className="space-y-6">
            <div>
              <Label htmlFor="s-email">E-Transfer Email (where customers send money)</Label>
              <Input id="s-email" data-testid="settings-etransfer-email" value={form.etransfer_email} onChange={set("etransfer_email")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="s-name">Recipient Name</Label>
              <Input id="s-name" data-testid="settings-etransfer-name" value={form.etransfer_name} onChange={set("etransfer_name")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="s-instructions">Payment Instructions</Label>
              <Textarea id="s-instructions" data-testid="settings-instructions" value={form.instructions} onChange={set("instructions")} className="mt-1" rows={5} />
            </div>

            <div data-testid="admin-etransfer-steps">
              <Label>Checkout Payment Steps</Label>
              <p className="text-xs text-[#6E675F] mt-1 mb-3">
                Shown as numbered steps on checkout and order confirmation. Placeholders are filled automatically:
                {" "}<code className="bg-[#EFEBE6] px-1 rounded">{"{AMOUNT}"}</code>
                {" "}<code className="bg-[#EFEBE6] px-1 rounded">{"{ETRANSFER_EMAIL}"}</code>
                {" "}<code className="bg-[#EFEBE6] px-1 rounded">{"{RECIPIENT_NAME}"}</code>
                {" "}<code className="bg-[#EFEBE6] px-1 rounded">{"{YOUR_EMAIL}"}</code>
                {" "}<code className="bg-[#EFEBE6] px-1 rounded">{"{ORDER_NUMBER}"}</code>
              </p>
              <div className="space-y-2">
                {form.etransfer_steps.map((s, i) => (
                  <div key={s._key} className="flex gap-2 items-start" data-testid={`etransfer-step-row-${i}`}>
                    <span className="shrink-0 w-7 h-7 mt-1 rounded-full bg-[#EFEBE6] text-[#6E675F] text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <Textarea rows={2} value={s.text} onChange={(e) => setStep(i, e.target.value)} data-testid={`etransfer-step-input-${i}`} className="flex-1" />
                    <Button size="icon" variant="outline" onClick={() => removeStep(i)} data-testid={`etransfer-step-remove-${i}`} className="text-red-700 border-red-200 hover:bg-red-50 mt-1">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {form.etransfer_steps.length === 0 && (
                  <p className="text-sm text-[#6E675F]">No steps — the default 3 steps will be shown.</p>
                )}
                <Button variant="outline" onClick={addStep} data-testid="add-etransfer-step-button" className="mt-1">
                  <Plus className="w-4 h-4 mr-1" /> Add Step
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-[#DDD7CF] rounded-lg bg-[#EFEBE6]/60 p-6 flex items-start gap-3" data-testid="etransfer-settings-restricted">
          <Lock className="w-5 h-5 text-[#8C7B6B] shrink-0 mt-0.5" />
          <p className="text-sm text-[#6E675F]">
            <strong className="text-[#1A1A1A]">E-Transfer payment settings</strong> are restricted to Super Admins and the General Admin.
          </p>
        </div>
      )}

      <ShippingSettingsCard form={form} setForm={setForm} set={set} />

      <div className="border border-[#DDD7CF] rounded-lg bg-white p-8" data-testid="admin-volume-discounts">
        <h2 className="font-display text-lg uppercase tracking-wide text-[#1A1A1A] mb-2">Volume Discounts (Store Default)</h2>
        <p className="text-sm text-[#6E675F] mb-6">Default tiers applied per product based on its quantity in an order. Products with their own tiers (set in the product editor) override these. Highest matching tier wins.</p>

        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_1fr_auto] gap-3 text-xs tracking-[0.1em] uppercase font-semibold text-[#6E675F]">
            <span>Min Quantity</span><span>Discount %</span><span></span>
          </div>
          {form.volume_discounts.map((t, i) => (
            <div key={t._key || `${t.min_qty}-${t.percent}`} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center" data-testid={`volume-tier-${i}`}>
              <Input type="number" min="1" value={t.min_qty} onChange={(e) => setTier(i, "min_qty", e.target.value)} data-testid={`volume-tier-qty-${i}`} placeholder="e.g. 3" />
              <Input type="number" min="0" step="0.5" value={t.percent} onChange={(e) => setTier(i, "percent", e.target.value)} data-testid={`volume-tier-percent-${i}`} placeholder="e.g. 5" />
              <Button size="icon" variant="outline" onClick={() => removeTier(i)} data-testid={`volume-tier-remove-${i}`} className="text-red-700 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {form.volume_discounts.length === 0 && (
            <p className="text-sm text-[#6E675F]">No volume discounts configured.</p>
          )}
          <Button variant="outline" onClick={addTier} data-testid="add-volume-tier-button" className="mt-2">
            <Plus className="w-4 h-4 mr-1" /> Add Tier
          </Button>
        </div>
      </div>

      <div className="border border-[#DDD7CF] rounded-lg bg-white p-8" data-testid="admin-storefront-settings">
        <h2 className="font-display text-lg uppercase tracking-wide text-[#1A1A1A] mb-2">Storefront</h2>
        <p className="text-sm text-[#6E675F] mb-6">Control which sections appear on the shop homepage.</p>
        <div className="flex items-center gap-3">
          <Switch
            id="s-bundles"
            data-testid="settings-bundles-visible"
            checked={!!form.bundles_visible}
            onCheckedChange={(v) => setForm((f) => ({ ...f, bundles_visible: v }))}
          />
          <Label htmlFor="s-bundles">Show the Bundles page (header link + homepage banner)</Label>
        </div>
      </div>

      <div className="border border-[#DDD7CF] rounded-lg bg-white p-8" data-testid="admin-notification-settings">
        <h2 className="font-display text-lg uppercase tracking-wide text-[#1A1A1A] mb-2">Notification Emails</h2>
        <p className="text-sm text-[#6E675F] mb-6">
          These addresses get an email the moment a <strong>new order</strong> is placed, when a <strong>new chat conversation</strong> starts,
          and when a customer <strong>replies to an unread chat</strong> (one alert per unread burst — opening the conversation in the Inbox resets it).
          Leave the list empty to notify every admin account instead.
        </p>
        <div className="space-y-2">
          {(form.notification_emails || []).map((e, i) => (
            <div key={e._key} className="flex gap-2 items-center" data-testid={`notification-email-row-${i}`}>
              <Input type="email" value={e.email} onChange={(ev) => setNotifyEmail(i, ev.target.value)} data-testid={`notification-email-input-${i}`} placeholder="alerts@example.com" className="flex-1" />
              <Button size="icon" variant="outline" onClick={() => removeNotifyEmail(i)} data-testid={`notification-email-remove-${i}`} className="text-red-700 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {(form.notification_emails || []).length === 0 && (
            <p className="text-sm text-[#6E675F]" data-testid="notification-emails-empty">No designated emails — all admin accounts are notified.</p>
          )}
          <Button variant="outline" onClick={addNotifyEmail} data-testid="add-notification-email-button" className="mt-1">
            <Plus className="w-4 h-4 mr-1" /> Add Email
          </Button>
        </div>
        <div className="mt-6 pt-5 border-t border-[#EFEBE6] flex flex-wrap items-center gap-3" data-testid="email-test-row">
          <Button variant="outline" onClick={sendTestEmail} disabled={testingEmail} data-testid="send-test-email-button">
            <Send className="w-4 h-4 mr-1" /> {testingEmail ? "Sending…" : "Send test email"}
          </Button>
          <p className="text-xs text-[#6E675F]">
            Sends a real message to <strong>{user?.email}</strong> so you can confirm order and alert emails are being delivered.
          </p>
          {emailTest && (
            <p className={`basis-full text-xs ${emailTest.ok ? "text-[#2D6A4F]" : "text-red-700"}`} data-testid="send-test-email-result">
              {emailTest.ok ? `Delivered to ${emailTest.to} from ${emailTest.sender}.` : emailTest.error}
            </p>
          )}
        </div>
      </div>

      <div className="border border-[#DDD7CF] rounded-lg bg-white p-8" data-testid="admin-low-stock-settings">
        <h2 className="font-display text-lg uppercase tracking-wide text-[#1A1A1A] mb-2">Low Stock Alerts</h2>
        <p className="text-sm text-[#6E675F] mb-6">
          Every admin gets an email the moment a product's stock drops to or below this number after a sale.
          The alert fires once per threshold crossing — restocking resets it. Set to 0 to turn alerts off.
        </p>
        <div className="max-w-xs">
          <Label htmlFor="s-lowstock">Alert Threshold (units)</Label>
          <Input id="s-lowstock" type="number" min="0" step="1" data-testid="settings-low-stock-threshold" value={form.low_stock_threshold ?? 5} onChange={set("low_stock_threshold")} className="mt-1" />
        </div>
      </div>

      <div className="border border-[#DDD7CF] rounded-lg bg-white p-8" data-testid="admin-card-fee-settings">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-lg uppercase tracking-wide text-[#1A1A1A]">Debit Card + Crypto (Mintage Pay)</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#6E675F]">{form.card_payments_enabled ? "Enabled" : "Disabled"}</span>
            <Switch
              checked={!!form.card_payments_enabled}
              onCheckedChange={(v) => setForm((f) => ({ ...f, card_payments_enabled: v }))}
              data-testid="settings-card-payments-toggle"
            />
          </div>
        </div>
        <p className="text-sm text-[#6E675F] mb-6">
          {form.card_payments_enabled
            ? "Customers can pay by debit card or crypto (USDC/USDT) at checkout, in a secure Mintage Pay window. The processing fee below is added on top of the order total for card orders only."
            : "Card and crypto payments are switched off — customers only see Interac e-Transfer at checkout. Flip the toggle to offer debit card and crypto payments."}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="s-cardpct">Fee Percent (%)</Label>
            <Input id="s-cardpct" type="number" min="0" step="0.1" data-testid="settings-card-fee-percent" value={form.card_fee_percent ?? 2.9} onChange={set("card_fee_percent")} disabled={!form.card_payments_enabled} className="mt-1 disabled:opacity-50" />
          </div>
          <div>
            <Label htmlFor="s-cardfixed">Fixed Fee (CAD)</Label>
            <Input id="s-cardfixed" type="number" min="0" step="0.05" data-testid="settings-card-fee-fixed" value={form.card_fee_fixed ?? 0.3} onChange={set("card_fee_fixed")} disabled={!form.card_payments_enabled} className="mt-1 disabled:opacity-50" />
          </div>
        </div>
        {form.card_payments_enabled && (
          <p className="text-xs text-[#6E675F] mt-4" data-testid="card-fee-example">
            Example: a $100 card order is charged ${(100 + 100 * (parseFloat(form.card_fee_percent) || 0) / 100 + (parseFloat(form.card_fee_fixed) || 0)).toFixed(2)} total.
          </p>
        )}
      </div>

      <div className="border border-[#DDD7CF] rounded-lg bg-white p-8" data-testid="admin-rewards-settings">
        <h2 className="font-display text-lg uppercase tracking-wide text-[#1A1A1A] mb-2">Rewards Program</h2>
        <p className="text-sm text-[#6E675F] mb-6">Customers earn points when their order is verified and can redeem them for a discount at checkout.</p>
        <div className="flex items-center gap-3 mb-6">
          <Switch
            id="s-rewards"
            data-testid="settings-rewards-enabled"
            checked={!!form.rewards_enabled}
            onCheckedChange={(v) => setForm((f) => ({ ...f, rewards_enabled: v }))}
          />
          <Label htmlFor="s-rewards">Rewards program enabled</Label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="s-earn">Points earned per $1 spent</Label>
            <Input id="s-earn" type="number" min="0" step="0.5" data-testid="settings-rewards-earn-rate" value={form.rewards_earn_rate ?? 1} onChange={set("rewards_earn_rate")} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="s-redeem">Points needed for $1 off</Label>
            <Input id="s-redeem" type="number" min="1" step="1" data-testid="settings-rewards-redeem-rate" value={form.rewards_redeem_rate ?? 100} onChange={set("rewards_redeem_rate")} className="mt-1" />
          </div>
        </div>
        <p className="text-xs text-[#6E675F] mt-4" data-testid="rewards-example">
          Example: a verified $100 order earns {Math.round(100 * (parseFloat(form.rewards_earn_rate) || 0))} points
          {" "}= ${(100 * (parseFloat(form.rewards_earn_rate) || 0) / (parseFloat(form.rewards_redeem_rate) || 100)).toFixed(2)} off a future order.
        </p>
      </div>

      <Button data-testid="settings-save-button" onClick={save} disabled={saving} className="bg-[#8C7B6B] text-white hover:bg-[#75665A]">
        {saving ? "Saving…" : "Save All Settings"}
      </Button>
    </div>
  );
};
